/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { Component } from 'react';
import { omit } from 'lodash';
import PropTypes from 'prop-types';

import './draggable.scss';

export type Position = {
	top?: number;
	right?: number;
	left?: number;
	bottom?: number;
};

export default function draggable(position: Position = {}, boundaryElement) {
	return function(ComponentClass) {
		class Draggable extends Component<any, any> {
			static propTypes = {
				draggableForwardRef: PropTypes.any,
			};

			componentWillUnmount() {
				this._closeDragElement();
			}

			public static WrappedComponent: Component;
			_startPositionX = 0;
			_startPositionY = 0;
			_endPositionX = 0;
			_endPositionY = 0;
			_el: HTMLElement;

			_closeDragElement = () => {
				// stop moving when mouse button is released:
				document.removeEventListener('mouseup', this._closeDragElement);
				document.removeEventListener('mousemove', this._elementDrag);
			};

			_elementDrag = e => {
				const evt = e || window.event;

				evt.preventDefault();

				const boundaryRect = (
					boundaryElement || this._el.parentElement
				).getBoundingClientRect();
				const elementRect = this._el.getBoundingClientRect();

				this._startPositionX = this._endPositionX - evt.clientX;
				this._startPositionY = this._endPositionY - evt.clientY;

				const newTop = this._el.offsetTop - this._startPositionY;
				const newLeft = this._el.offsetLeft - this._startPositionX;

				// boundary check
				if (newTop > 0 && newTop + elementRect.height < boundaryRect.height) {
					this._el.style.top = `${newTop}px`;
					this._el.style.bottom = '';
					this._endPositionY = evt.clientY;
				}
				if (newLeft > 0 && newLeft + elementRect.width < boundaryRect.width) {
					this._el.style.left = `${newLeft}px`;
					this._el.style.right = '';
					this._endPositionX = evt.clientX;
				}
			};

			_dragMouseDown = e => {
				const evt = e || window.event;

				evt.preventDefault();

				this._endPositionX = evt.clientX;
				this._endPositionY = evt.clientY;

				document.addEventListener('mouseup', this._closeDragElement);
				document.addEventListener('mousemove', this._elementDrag);
			};

			render() {
				const passThroughProps = omit(this.props, 'draggableForwardRef');
				const { top, right, left, bottom } = position;

				return (
					<div
						className="bi-common-draggable"
						onMouseDown={this._dragMouseDown}
						ref={el => (this._el = el)}
						style={{
							top,
							right,
							left,
							bottom,
						}}
					>
						{/* to seize the mouseDown event */}
						<div
							onMouseDown={e => e.stopPropagation()}
							className="bi-common-draggable__innerContainer"
						>
							<ComponentClass
								{...passThroughProps}
								ref={this.props.draggableForwardRef}
							/>
						</div>
					</div>
				);
			}
		}

		Draggable.WrappedComponent = ComponentClass;

		return React.forwardRef((props, ref) => <Draggable {...props} draggableForwardRef={ref} />);
	};
}
